/**
 * ShelfNavigation — refined shelf tabs with a sliding active indicator.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { LayoutGrid, Bookmark, BookOpen, CheckCircle2, Heart, Sparkles, Plus } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import { BOOKSHELF } from '@/constants/testIds';

const SYSTEM_ICONS = {
  shelf_all: LayoutGrid,
  shelf_tbr: Bookmark,
  shelf_reading: BookOpen,
  shelf_finished: CheckCircle2,
  shelf_favorites: Heart,
};

function ShelfTab({ shelf, isActive, onClick }) {
  const Icon = SYSTEM_ICONS[shelf.id] ?? Sparkles;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      className={cx(
        'group relative flex shrink-0 snap-start items-center gap-1.5 whitespace-nowrap px-3.5 py-2.5 text-sm font-medium transition-colors duration-200',
        isActive ? 'text-coral' : 'text-subtle hover:text-ink'
      )}
    >
      {shelf.type === 'custom' ? (
        <span className="text-[15px] leading-none">{shelf.emoji || <Icon className="h-4 w-4" />}</span>
      ) : (
        <Icon className={cx('h-4 w-4', isActive ? 'text-coral' : 'text-subtle group-hover:text-ink')} />
      )}
      <span>{shelf.name}</span>
      {shelf.bookCount > 0 && (
        <span className={cx('text-xs', isActive ? 'text-coral/70' : 'text-subtle/70')}>{shelf.bookCount}</span>
      )}
      {isActive && (
        <motion.span
          layoutId="shelf-tab-underline"
          className="absolute -bottom-px left-2 right-2 h-[2.5px] rounded-full bg-coral"
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
        />
      )}
    </button>
  );
}

export default function ShelfNavigation({
  shelves,
  activeShelf,
  onShelfChange,
  onCreateShelfClick,
}) {
  const defaultShelves = shelves.filter(s => s.type === 'system');
  const customShelves = shelves.filter(s => s.type === 'custom');

  return (
    <nav
      className="u-container mt-8 border-b border-line"
      role="tablist"
      aria-label="Bookshelf sections"
      data-testid={BOOKSHELF.shelfNavigation}
    >
      <div className="scrollbar-none -mx-1 flex snap-x items-center gap-1 overflow-x-auto px-1">
        {defaultShelves.map(shelf => (
          <ShelfTab
            key={shelf.id}
            shelf={shelf}
            isActive={activeShelf === shelf.id}
            onClick={() => onShelfChange(shelf.id)}
          />
        ))}

        {customShelves.length > 0 && <span className="mx-1 h-5 w-px shrink-0 bg-line" aria-hidden="true" />}

        {customShelves.map(shelf => (
          <ShelfTab
            key={shelf.id}
            shelf={shelf}
            isActive={activeShelf === shelf.id}
            onClick={() => onShelfChange(shelf.id)}
          />
        ))}

        <button
          type="button"
          onClick={onCreateShelfClick}
          title="Create a new shelf"
          className="ml-1 flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3.5 py-2.5 text-sm font-medium text-subtle transition-colors hover:text-coral"
        >
          <Plus className="h-3.5 w-3.5" /> New Shelf
        </button>
      </div>
    </nav>
  );
}
